//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import 'github-markdown-css/github-markdown-light.css';
import '../../../styles/markdown-override.css';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { ThumbsUp, ThumbsDown, ChevronDown, Copy, Check } from 'lucide-react';
import AIChat from '../../../icons/AIChatLogo.svg';
import { useTranslation } from 'react-i18next';
import LoopDataService from '../../../utils/LoopDataService';
import { useUserAuth } from '../../../UserAuth';
import AnimatedAIIcon from './AnimatedAIIcon';
import { TRANSCRIPT_LOG_PREFIX } from './transcriptExport';
import { Button } from '../../ui/button';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '../../ui/collapsible';

interface AIResponseProps {
  message: string;
  subMessage?: React.ReactNode;
  loops?: { id: string; name: string }[];
  onLoopSelect?: (id: string) => void;
  onFeedback?: (type: 'up' | 'down') => void;
  isBufferMessage?: boolean;
  isAIBuffering?: boolean;
  loopId?: string;
  showFeedback?: boolean;
}

const AIResponse: React.FC<AIResponseProps> = ({
  message,
  subMessage,
  loops,
  onLoopSelect,
  onFeedback,
  isBufferMessage = false,
  isAIBuffering = false,
  loopId,
  showFeedback = true,
}) => {
  const { t } = useTranslation();
  const LOG_PREFIX = TRANSCRIPT_LOG_PREFIX;
  const [selectedFeedback, setSelectedFeedback] = useState<
    'up' | 'down' | null
  >(null);
  const [copied, setCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    hoverTimerRef.current = setTimeout(() => setIsHovered(false), 200);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };
  const { user, isAuthenticated } = useUserAuth();

  // Create LoopDataService instance at component level
  const loopDataService = new LoopDataService(
    () => user,
    () => isAuthenticated,
  );

  // Check if this is a welcome/prompt message by checking for loops prop AND specific message patterns
  const isWelcomeMessage =
    (loops && loops.length > 0) ||
    message === t('ai-prompt-message') ||
    message === t('tooltip-no-loops');
  const isLogBlock = message.startsWith(LOG_PREFIX);

  const formatMessage = (msg: string) => {
    const lines = msg.split('\n').filter((line) => line.trim());

    const allLinesAreListItems = lines.every((line) =>
      line.trim().startsWith('- '),
    );

    if (allLinesAreListItems && lines.length > 0) {
      return (
        <ul id="id-ai-response-message" data-testid="id-ai-response-message">
          {lines.map((line, index) => (
            <li
              key={index}
              className="mb-1 font-[Roboto] text-[13px] leading-normal text-[#1C262E]"
            >
              <strong>{line.trim().substring(2)}</strong>
            </li>
          ))}
        </ul>
      );
    } else {
      return lines.map((line, index) => (
        <div
          id={`id-ai-response-format-line-${index}`}
          data-testid={`ai-response-format-line-${index}`}
          key={index}
        >
          <strong>{line}</strong>
          <br />
        </div>
      ));
    }
  };

  const handleFeedback = async (type: 'up' | 'down') => {
    setSelectedFeedback(type);

    if (loopId) {
      try {
        const yahOrNay = type === 'up' ? true : false;
        await loopDataService.captureAIFeedback(loopId, yahOrNay);
      } catch (error) {}
    }
  };

  if (isLogBlock) {
    const logContent = message.replace(LOG_PREFIX, '').trim();
    const lines = logContent.split('\n').filter((line) => line.trim());

    return (
      <div
        id="id-ai-response-log-container"
        data-testid="ai-response-log-container"
        className="relative flex w-full flex-nowrap items-start overflow-visible"
      >
        <span
          id="id-ai-response-log-icon-wrapper"
          data-testid="ai-response-log-icon-wrapper"
          title={
            isAIBuffering
              ? t('ai-thinking-tooltip', 'AI is thinking...')
              : undefined
          }
        >
          <AnimatedAIIcon
            src={AIChat}
            alt={t('ai-chat-logo')}
            style={{ width: '30px', height: '30px', marginRight: '2px' }}
            isProcessing={isAIBuffering}
          />
        </span>
        <div
          id="id-ai-response-log-message-box"
          data-testid="ai-response-log-message-box"
          className="ml-[10px] flex-1 border-none bg-transparent p-0 shadow-none"
          style={{ maxWidth: 'calc(100% - 42px)' }}
        >
          <Collapsible
            defaultOpen
            className="overflow-hidden rounded-lg border border-[#E0E0E0] bg-[#F8F9FA]"
          >
            <CollapsibleTrigger className="flex min-h-[44px] w-full items-center justify-between border-b border-[#E0E0E0] bg-transparent px-4 py-2.5 text-[14px] font-semibold text-[#444] hover:bg-gray-50 [&[data-state=open]_svg]:rotate-180">
              Show Thinking
              <ChevronDown className="size-4 transition-transform duration-200" />
            </CollapsibleTrigger>
            <CollapsibleContent className="flex flex-col gap-2 bg-white px-4 py-3">
              {lines.map((line, index) => (
                <div
                  id={`id-ai-response-log-line-${index}`}
                  data-testid={`ai-response-log-line-${index}`}
                  key={index}
                  className="text-[13px] leading-normal text-[#1C262E]"
                >
                  {line}
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
        </div>
      </div>
    );
  }

  return (
    <div
      id="id-ai-response-container"
      data-testid="ai-response-container"
      className="relative flex w-full flex-nowrap items-start overflow-visible"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span
        id="id-ai-response-icon-wrapper"
        data-testid="ai-response-icon-wrapper"
        title={
          isAIBuffering
            ? t('ai-thinking-tooltip', 'AI is thinking...')
            : undefined
        }
      >
        <AnimatedAIIcon
          src={AIChat}
          alt={t('ai-chat-logo')}
          isProcessing={isAIBuffering}
          style={{ width: '30px', height: '30px', marginRight: '2px' }}
        />
      </span>

      <div
        id="id-ai-response-message-box"
        data-testid="ai-response-message-box"
        className="ml-[10px] flex-1 overflow-visible break-words text-left"
        style={{
          backgroundColor: '#F2F4F5',
          padding: '10px 14px',
          borderRadius: '8px',
          maxWidth: 'calc(100% - 42px)',
          minHeight: 'auto',
          height: 'auto',
        }}
      >
        <div
          className="markdown-body"
          style={{
            fontSize: '13px',
            fontFamily: 'Roboto, sans-serif',
            backgroundColor: 'transparent',
            color: '#1C262E',
            lineHeight: '1.5',
          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            components={{
              code({ className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || '');
                if (match) {
                  return (
                    <SyntaxHighlighter
                      language={match[1]}
                      style={docco}
                      customStyle={{
                        background: '#fff',
                        color: '#1f1f1f',
                        fontSize: '1em',
                        borderRadius: '6px',
                        padding: '1em',
                        margin: '1em 0',
                      }}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  );
                }
                return (
                  <code
                    style={{
                      background: '#fff',
                      color: '#c7254e',
                      fontSize: '0.95em',
                      borderRadius: '4px',
                      padding: '0.2em 0.4em',
                      fontFamily: 'monospace',
                    }}
                    {...props}
                  >
                    {children}
                  </code>
                );
              },
              a: ({ node, ...props }) => (
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                <a target="_blank" rel="noopener noreferrer" {...props} />
              ),
            }}
          >
            {message}
          </ReactMarkdown>
        </div>

        {subMessage && (
          <div
            id="id-ai-response-submessage"
            data-testid="id-ai-response-submessage"
            className="mt-2 max-w-full overflow-visible break-words"
            style={{
              fontFamily: 'Roboto',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '20px',
              color: '#354958',
            }}
          >
            {subMessage}
          </div>
        )}

        {loops && loops.length > 0 && (
          <div
            id="id-ai-response-loops-container"
            data-testid="ai-response-loops-container"
            className="mt-3 flex flex-wrap gap-2.5 p-2.5"
          >
            {loops.map((loop) => (
              <button
                key={loop.id}
                onClick={() => onLoopSelect?.(loop.id)}
                className="cursor-pointer rounded-[5px] border-none bg-white text-center font-[Roboto] text-[14px] leading-[24px] text-black shadow-[0px_4px_4px_0px_rgba(0,0,0,0.07)] hover:bg-gray-50"
                style={{ padding: '8px 12px', margin: '4px 8px 4px 0' }}
              >
                {t(loop.name)}
              </button>
            ))}
          </div>
        )}
      </div>

      {!isBufferMessage && !isAIBuffering && !isWelcomeMessage && (
        <div
          id="id-ai-response-feedback-container"
          data-testid="ai-response-feedback-container"
          className="absolute flex items-center gap-0.5"
          style={{ bottom: '-24px', right: '0px' }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {isHovered && (
            <Button
              id="id-ai-response-copy-button"
              data-testid="ai-response-copy-button"
              variant="ghost"
              size="icon"
              onClick={handleCopy}
              title={t('copy', 'Copy')}
              className="size-6 p-0.5"
              style={{
                color: copied ? '#4caf50' : 'rgba(0,0,0,0.35)',
              }}
            >
              {copied ? (
                <Check data-testid="CheckIcon" className="size-4" />
              ) : (
                <Copy data-testid="ContentCopyIcon" className="size-4" />
              )}
            </Button>
          )}
          {showFeedback && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleFeedback('up')}
                className="size-6 p-0.5"
                style={{
                  color:
                    selectedFeedback === 'up'
                      ? 'rgba(0,0,0,0.7)'
                      : 'rgba(0,0,0,0.35)',
                }}
              >
                <ThumbsUp
                  data-testid="ThumbUpOutlinedIcon"
                  className="size-4"
                />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleFeedback('down')}
                className="size-6 p-0.5"
                style={{
                  color:
                    selectedFeedback === 'down'
                      ? 'rgba(0,0,0,0.7)'
                      : 'rgba(0,0,0,0.35)',
                }}
              >
                <ThumbsDown
                  data-testid="ThumbDownOutlinedIcon"
                  className="size-4"
                />
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AIResponse;
